import {TextScene} from "../../common/text";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {clearAllTimeouts} from "../../common/helpers";
import {usePolling} from "../../common/effects";
import {CountdownBadge} from "../../common/countdown";
import {countdownNow} from "../../common/showPause";

export const ChaosCharacters = ({round, isControl, roundEnd, clockDrift}) => {
    const [hurryUp, setHurryUp] = useState(false);
    useEffect(() => {
        const tick = () => {
            if (typeof roundEnd !== 'number' || roundEnd <= 0) {
                setHurryUp(false);
                return;
            }
            const remainingMs = roundEnd - countdownNow(clockDrift || 0);
            setHurryUp(remainingMs > 0 && remainingMs < 10000);
        };
        tick();
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [roundEnd, clockDrift]);


    if(isControl) return <ChaosCharactersTally round={round} />
    else return <ChaosCharactersPlayer round={round} roundEnd={roundEnd} clockDrift={clockDrift} />;
}

const CHAOS_GLITCH_SHADOW = '0 0 8px rgba(230,41,158,0.55), 2px 2px 0 rgba(0,0,0,0.95)';
const CHAOS_PINK = '#e6299e';
const CHAOS_PINK_DEEP = '#a8005f';
const CHAOS_GREEN = '#0aff35';
const CHAOS_RED = '#ff1a3a';
const CHAOS_CYAN = '#00f0ff';
const STROKE_FILTER = 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)';

const ChaosBackdrop = () => {
    const videoRef = useRef(null);
    useEffect(() => {
        const v = videoRef.current;
        if (v) v.play().catch(() => {});
    }, []);
    return <>
        <video
            ref={videoRef}
            src="/chaos-characters-assets/backdrop.mp4"
            poster="/chaos-characters-assets/backdrop.webp"
            autoPlay loop muted playsInline preload="auto"
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                backgroundImage: 'url(/chaos-characters-assets/backdrop.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: -2,
                pointerEvents: 'none',
            }}
        />
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(230,41,158,0), rgba(230,41,158,1))',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>
    </>;
};

const ChaosCharactersPlayer = ({round, roundEnd, clockDrift}) => {
    const [target, setTarget] = useState();
    const [score, setScore] = useState();
    const [answer, setAnswer] = useState("");

    const getScore = useCallback(() => {
        fetch('/chaos-characters/get-score', {credentials: 'include'})
            .then(response => response.json())
            .then(setScore)
    }, []);
    usePolling(getScore, 3000);

    const fetchTarget = () => {
        fetch('/chaos-characters/get-target?' + new URLSearchParams({round}), {credentials: 'include'})
            .then(response => response.json())
            .then(setTarget)
    }

    useEffect(() => {
        setTarget(null);
        fetchTarget()
    }, [round]);

    const getPrompt = () => {
        if(!target) return null;
        const promptLineStyle = {
            fontSize: 26,
            fontWeight: 800,
            fontStyle: 'italic',
            lineHeight: 1.25,
            letterSpacing: 1,
            color: '#fff',
            textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
        };
        const targetName = (text) => <span style={{color: CHAOS_PINK, textShadow: CHAOS_GLITCH_SHADOW}}>{text}</span>;
        switch(round) {
            case 0:
                return <span style={promptLineStyle}>What is <em>your</em> nickname?</span>;
            case 1:
                return <span style={promptLineStyle}>What is {targetName(target.target_one.name)}'s nickname?</span>;
            case 2:
                return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16}}>
                    <span style={promptLineStyle}>What is the nickname of:</span>
                    <img src={target.target_one.image_url} style={{width: 180, height: 180, objectFit: 'cover', filter: STROKE_FILTER, willChange: 'filter'}}/>
                </div>;
            case 3:
                return <span style={promptLineStyle}>Tell {targetName(target.target_one.name)} to submit {targetName(target.target_two?.name)}'s nickname.</span>;
            case 4:
                return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14}}>
                    <span style={promptLineStyle}>Tell {targetName(target.target_one.nickname)} to submit the nickname of:</span>
                    <img src={target.target_two?.image_url} style={{width: 180, height: 180, objectFit: 'cover', filter: STROKE_FILTER, willChange: 'filter'}}/>
                </div>;
        }
    }

    const submitAnswer = () => {
        setTarget(null);
        fetch('/chaos-characters/submit-answer', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({round, answer})
        }).then(() => {
            fetchTarget();
            getScore();
            setAnswer("");
        })
    }

    if (!target) return null;

    const getInteractive = () => {
        if(!target.answer) return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, width: '100%', maxWidth: 360}}>
            <input
                type="text"
                placeholder="Answer"
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                style={{
                    width: '100%',
                    padding: '16px 18px',
                    fontSize: 28,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    background: 'rgba(0,0,0,0.95)',
                    color: '#fff',
                    border: `2px solid ${CHAOS_PINK}`,
                    borderRadius: 8,
                    outline: 'none',
                    textAlign: 'center',
                    textTransform: 'capitalize',
                    boxShadow: `0 0 18px ${CHAOS_PINK}55, inset 0 1px 0 rgba(255,255,255,0.08)`,
                    boxSizing: 'border-box',
                }}
            />
            <button
                onClick={submitAnswer}
                style={{
                    width: '100%',
                    padding: '20px 0',
                    fontSize: 30,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    fontFamily: 'inherit',
                    letterSpacing: 4,
                    textTransform: 'uppercase',
                    color: '#2a0010',
                    background: `linear-gradient(180deg, ${CHAOS_PINK} 0%, ${CHAOS_PINK_DEEP} 100%)`,
                    border: 'none',
                    borderRadius: 10,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -3px 0 rgba(0,0,0,0.35), 0 0 24px ${CHAOS_PINK}aa, 4px 4px 0 rgba(0,0,0,0.6)`,
                    cursor: 'pointer',
                    textShadow: '1px 1px 0 rgba(255,255,255,0.25)',
                }}
            >Submit</button>
        </div>;

        const correct = target.correct;
        return <div style={{
            textAlign: 'center',
            padding: '24px 32px',
            border: `3px solid ${correct ? CHAOS_GREEN : CHAOS_RED}`,
            borderRadius: 10,
            background: 'rgba(0,0,0,0.95)',
            boxShadow: `0 0 28px ${correct ? CHAOS_GREEN : CHAOS_RED}aa, 4px 4px 0 rgba(0,0,0,0.6)`,
        }}>
            <div style={{
                fontSize: 36,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 2,
                color: '#fff',
                textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
                marginBottom: 8,
            }}>{target.answer}</div>
            <div style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: 4,
                textTransform: 'uppercase',
                color: correct ? CHAOS_GREEN : CHAOS_RED,
                textShadow: `0 0 14px ${correct ? CHAOS_GREEN : CHAOS_RED}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
            }}>{correct ? '✓ Correct' : '✗ Wrong'}</div>
        </div>;
    }

    return <div style={{
        position: 'relative',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        width: '100%',
        boxSizing: 'border-box',
        paddingTop: 'env(safe-area-inset-top)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
        color: 'white',
    }}>
        <ChaosBackdrop darken={0.2}/>
        <CountdownBadge roundEnd={roundEnd} clockDrift={clockDrift}/>

        {/* Spacer pushes the all-in-one card to the bottom */}
        <div style={{flex: 1, minHeight: 20}}/>

        {/* All-in-one card: round/pts + You Are + prompt + interactive */}
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 18,
            width: 'calc(100% - 40px)',
            maxWidth: 420,
            padding: '18px 22px 22px',
            background: 'rgba(0,0,0,0.95)',
            border: `2px solid ${CHAOS_PINK}`,
            borderRadius: 8,
            boxShadow: `0 0 22px ${CHAOS_PINK}66, inset 0 1px 0 rgba(255,255,255,0.06), 4px 4px 0 rgba(0,0,0,0.6)`,
            margin: `0 0 calc(20px + env(safe-area-inset-bottom))`,
            boxSizing: 'border-box',
        }}>
            {/* Round + pts row */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                paddingBottom: 10,
                borderBottom: `1px solid ${CHAOS_PINK}55`,
            }}>
                <div style={{
                    fontSize: 15,
                    fontWeight: 800,
                    letterSpacing: 4,
                    textTransform: 'uppercase',
                    color: CHAOS_PINK,
                    textShadow: CHAOS_GLITCH_SHADOW,
                }}>Round {round + 1}</div>
                <div style={{
                    fontSize: 14,
                    fontWeight: 800,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    opacity: 0.95,
                    textShadow: '1px 1px 0 rgba(0,0,0,0.95)',
                }}>
                    <span style={{color: CHAOS_CYAN}}>{score?.score ?? 0}</span> pts
                    {' '}· <span style={{color: CHAOS_PINK}}>#{score?.rank ?? '–'}</span>
                </div>
            </div>

            {/* You Are row */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                width: '100%',
                position: 'relative',
                boxSizing: 'border-box',
                borderBottom: `1px solid ${CHAOS_PINK}55`,
                marginTop: -18,
            }}>
                <img
                    src={target.you.image_url}
                    alt={target.you.name}
                    style={{
                        width: 160,
                        height: 160,
                        objectFit: 'cover',
                        objectPosition: 'top',
                        alignSelf: 'stretch',
                        flexShrink: 0,
                        display: 'block',
                        filter: STROKE_FILTER,
                        willChange: 'filter',
                    }}
                />
                <div style={{minWidth: 0, flex: 1, textAlign: 'left'}}>
                    <div style={{
                        fontSize: 16,
                        fontWeight: 800,
                        letterSpacing: 5,
                        textTransform: 'uppercase',
                        color: CHAOS_PINK,
                        opacity: 0.9,
                        textShadow: '1px 1px 0 rgba(0,0,0,0.95)',
                        marginBottom: 4,
                    }}>You Are</div>
                    <div style={{
                        fontSize: 28,
                        fontWeight: 900,
                        fontStyle: 'italic',
                        lineHeight: 1.05,
                        textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>{target.you.name}</div>
                    <div style={{
                        fontSize: 18,
                        fontStyle: 'italic',
                        opacity: 0.92,
                        lineHeight: 1.2,
                        textShadow: '1px 1px 0 rgba(0,0,0,0.95)',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                    }}>{target.you.nickname}</div>
                </div>
            </div>

            {/* Prompt */}
            <div style={{
                textAlign: 'center',
                width: '100%',
            }}>{getPrompt()}</div>

            {/* Interactive */}
            {getInteractive()}
        </div>
    </div>
}

const ChaosCharactersTally = ({round}) => {
    const [results, setResults] = useState();

    const pollResults = () => {
        fetch('/chaos-characters/get-results')
            .then(response => response.json())
            .then((results) => {
                setResults(results);
                setTimeout(pollResults, 2000);
            })
    }

    useEffect(() => {
        pollResults();
        return clearAllTimeouts;
    }, []);

    if (!results) return null;

    const scores = results.correct_answers.map((answers, index) => {
        if (round >= index) {
            return <>
                <div style={{width: '200px', color: '#87ff00'}}>{results.correct_answers[index]}</div>
                <div style={{width: '200px', color: '#ff0000'}}>{results.incorrect_answers[index]}</div>
                <div style={{width: '200px'}}>{results.total - results.correct_answers[index] - results.incorrect_answers[index]}</div>
                <div style={{width: '200px'}}>{Math.round((results.correct_answers[index] / results.total) * 100)}%</div>
            </>;
        } else {
            return '-';
        }
    })

    const roundStyle = (scoreRound) => {
        const style = {
            border: '1px solid white',
            padding: 10,
            margin: 6,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '75%'
        };

        if(scoreRound === round) {
            return { ...style, borderColor: '#b4ff9b', border: 'solid 5px' }
        } else {
            return style;
        }
    }

    return <div style={{height: '100%', width: '100%', textAlign: 'center'}}>
        <div className="evenly-distribute-vertical" style={{paddingTop: 0, justifyContent: 'flex-start'}}>
            <div style={{width: '100%'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: 78, marginTop: 10}}>
                    <div style={{position: 'relative', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '75%'}}>
                        <img src="/chaos-characters-assets/logo.webp" alt="Chaos Characters" style={{height: 200, marginBottom: -20, position: 'relative', zIndex: 1, filter: 'drop-shadow(4px 4px 0 rgba(0,0,0,0.8))'}}/>
                        <div className={`grade-${results.grade}`} style={{position: 'absolute', right: 0, padding: 10}}>
                            <div style={{fontSize: 30}}>GRADE</div>
                            <div style={{fontWeight: 'bold', fontSize: 60}}>{results.grade}</div>
                        </div>
                    </div>
                    <div style={{fontSize: 36, width: '75%',display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10}}>
                        <div style={{width: '200px'}}></div>
                        <div style={{width: '200px'}}>Correct</div>
                        <div style={{width: '200px'}}>Incorrect</div>
                        <div style={{width: '200px'}}>No Answer</div>
                        <div style={{width: '200px'}}>% Correct</div>
                    </div>
                    <div style={roundStyle(0)}>
                        <div style={{width: '200px', fontSize: 46}}>Round 1</div>
                        {scores[0]}
                    </div>
                    <div style={roundStyle(1)}>
                        <div style={{width: '200px', fontSize: 46}}>Round 2</div>
                        {scores[1]}
                    </div>
                    <div style={roundStyle(2)}>
                        <div style={{width: '200px', fontSize: 46}}>Round 3</div>
                        {scores[2]}
                    </div>
                    <div style={roundStyle(3)}>
                        <div style={{width: '200px', fontSize: 46}}>Round 4</div>
                        {scores[3]}
                    </div>
                    <div style={roundStyle(4)}>
                        <div style={{width: '200px', fontSize: 46}}>Round 5</div>
                        {scores[4]}
                    </div>
                </div>
            </div>
        </div>
    </div>;
}

export const GradeIcon = () => {

}

export const Grade = ({isControl}) => {

    const [results, setResults] = useState();

    useEffect(() => {
        fetch('/chaos-characters/get-results')
            .then(response => response.json())
            .then(setResults);
    }, []);

    if(!results) return null;

    if(isControl) return <div style={{textAlign: 'center', fontSize: 80, flexDirection: 'column', width: '1500px', height: '900px', display: 'flex', alignItems: 'center', alignSelf: 'flex-start', justifyContent: 'center'}}>
        <div className={`grade-${results.grade}`} style={{textAlign: 'center',  padding: 10}}>
            <div style={{fontSize: 160}}>GRADE</div>
            <div style={{fontWeight: 'bold', fontSize: 320}}>{results.grade}</div>
        </div>
    </div>;

    return <div className={`grade-${results.grade}`} style={{textAlign: 'center', padding: 10}}>
        <div style={{fontSize: 40}}>GRADE</div>
        <div style={{fontWeight: 'bold', fontSize: 80}}>{results.grade}</div>
    </div>;
}

export const ChaosCharactersInstructions = () => {
    const paragraph = {
        fontSize: 'clamp(22px, 3vw, 44px)',
        fontWeight: 800,
        lineHeight: 1.3,
        letterSpacing: 4,
        textTransform: 'uppercase',
        textShadow: '2px 2px 0 rgba(0,0,0,0.95)',
        marginBottom: 32,
    };
    return <div style={{
        width: '100%',
        height: '100%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 32px',
        boxSizing: 'border-box',
        color: '#fff',
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(/text-bg.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    }}>
        <div style={{maxWidth: 900}}>
            <p style={paragraph}>Earn points for correct answers, and by helping others answer questions involving you.</p>
            <p style={{
                marginTop: 48,
                fontSize: 'clamp(32px, 4.5vw, 72px)',
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 8,
                textTransform: 'uppercase',
                color: CHAOS_PINK,
                textShadow: `0 0 18px ${CHAOS_PINK}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
            }}>Good Luck.</p>
        </div>
    </div>;
};

export const ChaosCharactersTitle = ({isControl, name, r, g, b}) => {
    return <TextScene isControl={isControl} text={<span className={'title-card'}>🦹🏽<br/>Chaos Characters</span>}/>;
}